import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { telLink } from "@/lib/site-utils";
import { CONTACT } from "@/data/site";

const LINKS = [
  { id: "construction", label: "Construction" },
  { id: "travels", label: "Travels" },
  { id: "fleet", label: "Fleet" },
  { id: "gallery", label: "Gallery" },
  { id: "booking", label: "Booking" },
  { id: "reviews", label: "Reviews" },
  { id: "contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-300 ${
        scrolled ? "bg-asphalt/90 backdrop-blur-md border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1600px] px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="logo-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <span className="grid place-items-center h-9 w-9 bg-jcb text-black font-khand font-700 text-lg">SS</span>
          <span className="font-khand font-600 tracking-tight text-lg leading-none text-left">
            SS GROUP
            <span className="block font-plex text-[9px] tracking-[0.25em] text-neutral-400">CONSTRUCTION · TRAVELS</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="font-plex text-xs tracking-[0.15em] uppercase text-neutral-300 hover:text-jcb transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-call-btn"
            href={telLink()}
            className="hidden sm:flex items-center gap-2 bg-jcb text-black px-5 py-2.5 font-khand font-600 tracking-wide hover:bg-white transition-colors duration-200"
          >
            <Phone size={16} strokeWidth={2.5} />
            {CONTACT.phoneDisplay}
          </a>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden grid place-items-center h-10 w-10 border border-white/20 text-white"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden bg-asphalt border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-khand text-2xl py-2 border-b border-white/5 hover:text-jcb transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <a href={telLink()} className="mt-4 bg-jcb text-black text-center py-3 font-khand font-600 tracking-wide">
                Call {CONTACT.phoneDisplay}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
